/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { PageVisualsPanel } from "./PageVisuals";
import { Upload, Trash2, Image as ImageIcon, Megaphone, Flag, Package, X, Pencil, Check, Truck } from "lucide-react";
import { toast } from "sonner";
import { CoverMedia, coverSlides } from "./CoverMedia";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * CEO Visual Dashboard Manager
 * Jere twa seksyon ki afekte aparans Stocker Dashboard:
 *  1. Product Images (foto chak modèl vape — montre nan "My Stock")
 *  2. Dashboard Covers (General + USA + Haiti + DR, country-aware)
 *  3. Mini Announcements (anons ki parèt an ba Dashboard la)
 */

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const absUrl = (path) => path && path.startsWith("/api/") ? `${BACKEND_URL}${path}` : path;

// ============== PRODUCT IMAGES ==============
const ProductImagesPanel = () => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBase64, setPreviewBase64] = useState("");
  const [previewMime, setPreviewMime] = useState("image/png");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try { const r = await axios.get(`${API}/product-images`); setItems(r.data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Imaj twò gwo (max 10MB)");
    const dataUrl = await readFileAsBase64(f);
    setPreviewUrl(dataUrl);
    setPreviewBase64(dataUrl.split(",", 2)[1]);
    setPreviewMime(f.type || "image/png");
  };

  const save = async () => {
    if (!name.trim() || !previewBase64) return toast.error("Non modèl + foto obligatwa");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/ceo/product-images`, { name: name.trim(), image_base64: previewBase64, mime_type: previewMime });
      toast.success(r.data.updated ? `✅ Foto ${name} mete ajou` : `✅ ${name} ajoute`);
      setName(""); setPreviewUrl(""); setPreviewBase64("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
    finally { setSaving(false); }
  };

  const [editing, setEditing] = useState(null);
  const remove = async (id, nm) => {
    if (!window.confirm(`Efase foto ${nm}?`)) return;
    try { await axios.delete(`${API}/ceo/product-images/${id}`); toast.success("Efase"); load(); }
    catch (e) { toast.error("Erè"); }
  };

  return (
    <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-5" data-testid="product-images-panel">
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-5 h-5 text-amber-400" />
        <h3 className="text-white font-bold text-lg">Foto Pwodwi (Stock Product Images)</h3>
      </div>
      <p className="text-zinc-400 text-xs mb-4">Mete yon foto pou chak modèl vape. Lè w ajoute stock pou nenpòt stocker, foto a ap parèt otomatikman nan "My Stock" sou dashboard yo.</p>

      {/* Add form */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4 mb-4 flex flex-col md:flex-row gap-3 items-end">
        <div className="flex-1 w-full">
          <label className="block text-amber-300 text-xs font-bold mb-1 uppercase tracking-widest">Non Modèl</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="ex: Long 40K, Geek Bar 100K"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white outline-none focus:border-amber-500" data-testid="product-image-name-input" />
        </div>
        <div>
          <label className="block text-amber-300 text-xs font-bold mb-1 uppercase tracking-widest">Foto</label>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="product-image-file-input" />
          <button onClick={() => fileRef.current?.click()} className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-amber-400 rounded-lg px-4 py-2 text-sm flex items-center gap-2" data-testid="product-image-pick-btn">
            <Upload className="w-4 h-4" /> {previewUrl ? "Chanje" : "Chwazi"}
          </button>
        </div>
        {previewUrl && <img src={previewUrl} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-amber-500/40" />}
        <button onClick={save} disabled={saving || !name.trim() || !previewBase64}
          className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-black px-5 py-2 rounded-lg text-sm" data-testid="product-image-save-btn">
          {saving ? "..." : "Sove"}
        </button>
      </div>

      {/* List */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.length === 0 && <p className="text-zinc-500 text-sm col-span-full text-center py-6">Pa gen foto pwodwi ankò.</p>}
        {items.map(it => (
          <div key={it.id} className="bg-zinc-950/50 border border-zinc-800 rounded-xl p-2 group relative" data-testid={`product-image-item-${it.id}`}>
            <ProductThumb it={it} />
            <p className="text-white text-xs font-bold mt-1.5 text-center truncate">{it.name}</p>
            <button onClick={() => setEditing(it)} className="mt-1.5 w-full bg-amber-500 hover:bg-amber-400 text-black text-xs font-black rounded-lg py-1.5 flex items-center justify-center gap-1" data-testid={`product-image-edit-${it.id}`}><Pencil className="w-3 h-3" /> Modifier</button>
            <button onClick={() => remove(it.id, it.name)} className="absolute top-1 right-1 bg-red-600 hover:bg-red-500 text-white rounded-full p-1 opacity-80 hover:opacity-100 transition" data-testid={`product-image-del-${it.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      {editing && <ProductEditModal it={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const ProductThumb = ({ it }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [it.image_url]);
  return broken
    ? <div className="w-full h-28 rounded-lg bg-red-950/40 border border-red-500/40 flex items-center justify-center text-red-300 text-[11px] text-center px-2" data-testid={`product-image-broken-${it.id}`}>Photo introuvable — cliquez « Modifier » pour la renvoyer</div>
    : <img src={absUrl(it.image_url)} alt={it.name} onError={() => setBroken(true)} className="w-full h-28 object-contain rounded-lg bg-black/40" />;
};

const ProductEditModal = ({ it, onClose, onSaved }) => {
  const [name, setName] = useState(it.name);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const ref = useRef();
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Image trop grosse (max 10 Mo)");
    setPreview({ url: await readFileAsBase64(f), mime: f.type || "image/png" });
  };
  const save = async () => {
    if (!name.trim()) return toast.error("Le nom est obligatoire");
    setSaving(true);
    try {
      const r = await axios.put(`${API}/ceo/product-images/${it.id}`, { name: name.trim(), ...(preview ? { image_base64: preview.url.split(",", 2)[1], mime_type: preview.mime } : {}) });
      toast.success(r.data.renamed_in_stocks ? `Produit modifié — nom corrigé dans ${r.data.renamed_in_stocks} stock(s)` : "Produit modifié");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[150] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="product-edit-modal">
      <div className="bg-zinc-900 border border-amber-500/40 rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-white font-black text-lg">Modifier le produit</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="product-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <img src={preview?.url || absUrl(it.image_url)} alt="" className="w-full h-44 object-contain rounded-xl bg-black/50 mb-3" data-testid="product-edit-preview" />
        <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="product-edit-file" />
        <button onClick={() => ref.current?.click()} className="w-full bg-zinc-800 hover:bg-zinc-700 text-amber-400 text-sm font-bold rounded-lg py-2 mb-3 flex items-center justify-center gap-2" data-testid="product-edit-pick"><Upload className="w-4 h-4" /> Changer la photo</button>
        <label className="block text-amber-300 text-xs font-bold mb-1 uppercase tracking-widest">Nom du modèle</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-white mb-1 outline-none focus:border-amber-500" data-testid="product-edit-name" />
        <p className="text-zinc-500 text-[11px] mb-4">Le nouveau nom est aussi corrigé dans le stock des stockers qui ont ce produit.</p>
        <button onClick={save} disabled={saving} className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black rounded-lg py-2.5" data-testid="product-edit-save">{saving ? "..." : "Enregistrer"}</button>
      </div>
    </div>, document.body
  );
};

// ============== STOCK TRACKING MANAGER ==============
const TrackingManagerPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => axios.get(`${API}/ceo/tracking/list`).then(r => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const update = async (user_id, status, estimated_date = null, note = null) => {
    setLoading(true);
    try {
      await axios.post(`${API}/ceo/stocker/${user_id}/tracking`, { status, estimated_date, note });
      toast.success(`Tracking mete ajou: ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
    finally { setLoading(false); }
  };

  const promptUpdate = (it) => {
    const status = window.prompt("Status (preparing / on_the_way / delivered):", it.tracking_status || "preparing");
    if (!status || !["preparing","on_the_way","delivered"].includes(status.trim())) return;
    const date = window.prompt("Estime delivery date (YYYY-MM-DD):", it.tracking_estimated_date || "");
    const note = window.prompt("Nòt (opsyonèl):", it.tracking_note || "");
    update(it.id, status.trim(), date || null, note || null);
  };

  const stepColor = (s) => s === "preparing" ? "bg-amber-500" : s === "on_the_way" ? "bg-blue-500" : s === "delivered" ? "bg-emerald-500" : "bg-zinc-500";
  const stepLabel = (s) => s === "preparing" ? "🟡 Preparing" : s === "on_the_way" ? "🔵 On The Way" : s === "delivered" ? "🟢 Delivered" : "⚪ Pending";

  return (
    <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-5" data-testid="tracking-manager-panel">
      <div className="flex items-center gap-2 mb-3">
        <Truck className="w-5 h-5 text-amber-400" />
        <h3 className="text-white font-bold text-lg">Stock Tracking (Renouvèlman)</h3>
      </div>
      <p className="text-zinc-400 text-xs mb-4">Jere kote stock renouvèlman chak stocker/NM ye. 🟡 Preparing → 🔵 On the way → 🟢 Delivered. Moun yo wè l nan topbar yo.</p>
      {items.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-4">Pa gen moun k ap tann renouvèlman kounye a.</p>
      ) : (
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.id} className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`tracking-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm">{it.name} <span className="text-zinc-500 text-xs">· Kòd {it.code} · {it.user_type === "stocker" ? "Stocker" : "NM"} · {(it.country||"").toUpperCase()}</span></p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-black text-white px-2 py-0.5 rounded-full ${stepColor(it.tracking_status || "preparing")}`}>
                    {stepLabel(it.tracking_status || "preparing")}
                  </span>
                  {it.tracking_estimated_date && <span className="text-amber-300 text-[11px]">📅 Estime: {it.tracking_estimated_date}</span>}
                  {it.tracking_note && <span className="text-zinc-400 text-[11px] italic">{it.tracking_note}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => update(it.id, "preparing")} disabled={loading} className="text-[10px] bg-amber-500 hover:bg-amber-400 text-black font-black px-2 py-1 rounded" data-testid={`track-set-prep-${it.id}`}>🟡</button>
                <button onClick={() => update(it.id, "on_the_way")} disabled={loading} className="text-[10px] bg-blue-500 hover:bg-blue-400 text-white font-black px-2 py-1 rounded" data-testid={`track-set-way-${it.id}`}>🔵</button>
                <button onClick={() => update(it.id, "delivered")} disabled={loading} className="text-[10px] bg-emerald-500 hover:bg-emerald-400 text-white font-black px-2 py-1 rounded" data-testid={`track-set-del-${it.id}`}>🟢</button>
                <button onClick={() => promptUpdate(it)} disabled={loading} className="text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded" data-testid={`track-edit-${it.id}`} title="Modifye date/nòt"><Pencil className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== DASHBOARD COVERS ==============
const COVER_SLOTS = [
  { id: "general", label: "General Cover", flag: "🌐", desc: "Tout Stockers (fallback)" },
  { id: "usa", label: "USA Cover", flag: "🇺🇸", desc: "Stockers Etazini" },
  { id: "haiti", label: "Haiti Cover", flag: "🇭🇹", desc: "Stockers Ayiti" },
  { id: "dr", label: "Dominican Republic Cover", flag: "🇩🇴", desc: "Stockers Repiblik Dominikèn" },
];

const CHUNK = 4 * 1024 * 1024;
const uploadSlide = async (slotId, f, onProgress) => {
  const total = Math.max(1, Math.ceil(f.size / CHUNK));
  const uid = Array.from(crypto.getRandomValues(new Uint8Array(12))).map((b) => b.toString(16).padStart(2, "0")).join("");
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uid); fd.append("index", i); fd.append("total", total); fd.append("mime", f.type);
    fd.append("file", f.slice(i * CHUNK, (i + 1) * CHUNK), f.name);
    await axios.post(`${API}/ceo/dashboard-cover/${slotId}/media-chunk`, fd);
    onProgress(Math.round(((i + 1) / total) * 100));
  }
};

const SlideThumb = ({ sl, i, count, slotId, onMove, onRemove }) => (
  <div className="w-32" data-testid={`cover-slide-thumb-${slotId}-${i}`}>
    <div className="relative h-20 rounded-md overflow-hidden bg-black border border-zinc-700">
      {sl.type === "video" ? <video src={absUrl(sl.url)} muted className="w-full h-full object-cover" /> : <img src={absUrl(sl.url)} alt="" className="w-full h-full object-cover" />}
      <span className="absolute top-0.5 left-0.5 bg-amber-400 text-black text-[10px] font-black rounded px-1">{i + 1}{sl.type === "video" ? " ▶" : ""}</span>
      <button onClick={() => onRemove(i)} className="absolute top-0.5 right-0.5 bg-red-600 hover:bg-red-500 text-white rounded-full p-0.5" data-testid={`cover-slide-del-${slotId}-${i}`}><X className="w-2.5 h-2.5" /></button>
    </div>
    <div className="flex items-center gap-1 mt-1">
      <button disabled={i === 0} onClick={() => onMove(i, i - 1)} className="flex-1 h-8 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 text-amber-400 text-lg font-black rounded-lg" data-testid={`cover-slide-left-${slotId}-${i}`}>‹</button>
      <select value={i} onChange={(e) => onMove(i, parseInt(e.target.value, 10))} className="h-8 bg-zinc-900 border border-zinc-700 text-white text-sm rounded-lg px-1" data-testid={`cover-slide-pos-${slotId}-${i}`}>
        {Array.from({ length: count }, (_, k) => <option key={k} value={k}>N°{k + 1}</option>)}
      </select>
      <button disabled={i === count - 1} onClick={() => onMove(i, i + 1)} className="flex-1 h-8 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 text-amber-400 text-lg font-black rounded-lg" data-testid={`cover-slide-right-${slotId}-${i}`}>›</button>
    </div>
  </div>
);

const SlidesManager = ({ slot, cover, onSaved }) => {
  const slides = coverSlides(cover);
  const [progress, setProgress] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const ref = useRef();
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    if (f.size > (isVideo ? 50 : 10) * 1024 * 1024) return toast.error(`Fichier trop gros (max ${isVideo ? 50 : 10}MB)`);
    setLocalPreview({ url: URL.createObjectURL(f), video: isVideo });
    setProgress(0);
    try {
      await uploadSlide(slot.id, f, setProgress);
      toast.success("Sauvegardé — appliqué sur le tableau de bord de tout le monde");
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
    finally { setProgress(null); setLocalPreview(null); }
  };
  const remove = async (i) => {
    try { await axios.delete(`${API}/ceo/dashboard-cover/${slot.id}/media/${i}`); onSaved?.(); }
    catch { toast.error("Erreur"); }
  };
  const move = async (from, to) => {
    if (from === to) return;
    try { await axios.post(`${API}/ceo/dashboard-cover/${slot.id}/reorder`, { from_index: from, to_index: to, mode: "swap" }); toast.success(`N°${from + 1} ⇄ N°${to + 1} — places échangées`); onSaved?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="mb-3" data-testid={`cover-slides-${slot.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-amber-300 text-[10px] font-black uppercase tracking-widest">Diaporama · {slides.length} élément(s) · ordre d'affichage</p>
        <input ref={ref} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={onFile} className="hidden" data-testid={`cover-media-input-${slot.id}`} />
        <button onClick={() => ref.current?.click()} disabled={progress !== null} className="text-[11px] bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-amber-400 rounded-lg px-2.5 py-1 flex items-center gap-1" data-testid={`cover-media-add-${slot.id}`}>
          <Upload className="w-3 h-3" /> {progress !== null ? `${progress}%` : "Image / Vidéo"}
        </button>
      </div>
      <div className="flex gap-2 flex-wrap">
        {slides.map((sl, i) => <SlideThumb key={sl.url} sl={sl} i={i} count={slides.length} slotId={slot.id} onMove={move} onRemove={remove} />)}
        {localPreview && (
          <div className="relative w-32 h-20 rounded-md overflow-hidden bg-black border-2 border-amber-400" data-testid={`cover-local-preview-${slot.id}`}>
            {localPreview.video ? <video src={localPreview.url} muted className="w-full h-full object-cover" /> : <img src={localPreview.url} alt="" className="w-full h-full object-cover" />}
            <span className="absolute inset-0 bg-black/50 flex items-center justify-center text-amber-300 text-[10px] font-black">{progress ?? 0}%</span>
          </div>
        )}
        {!slides.length && !localPreview && <p className="text-zinc-500 text-[11px]">Aucun élément — ajoutez des images et/ou des vidéos (vidéo max 50 Mo).</p>}
      </div>
    </div>
  );
};

const CoverEditor = ({ slot, cover, onSaved }) => {
  const [title, setTitle] = useState(cover?.title || "");
  const [subtitle, setSubtitle] = useState(cover?.subtitle || "");
  const [ctaLabel, setCtaLabel] = useState(cover?.cta_label || "");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBase64, setPreviewBase64] = useState("");
  const [previewMime, setPreviewMime] = useState("image/png");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    setTitle(cover?.title || "");
    setSubtitle(cover?.subtitle || "");
    setCtaLabel(cover?.cta_label || "");
  }, [cover]);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Imaj twò gwo (max 10MB)");
    const dataUrl = await readFileAsBase64(f);
    setPreviewUrl(dataUrl);
    setPreviewBase64(dataUrl.split(",", 2)[1]);
    setPreviewMime(f.type || "image/png");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { title, subtitle, cta_label: ctaLabel };
      if (previewBase64) { payload.image_base64 = previewBase64; payload.mime_type = previewMime; }
      await axios.post(`${API}/ceo/dashboard-cover/${slot.id}`, payload);
      toast.success(`✅ ${slot.label} sove`);
      setPreviewUrl(""); setPreviewBase64("");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Efase ${slot.label}?`)) return;
    try { await axios.delete(`${API}/ceo/dashboard-cover/${slot.id}`); toast.success("Efase"); onSaved?.(); }
    catch { toast.error("Erè"); }
  };

  const hasSlides = coverSlides(cover).length > 0;

  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4" data-testid={`cover-slot-${slot.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{slot.flag}</span>
        <div className="flex-1">
          <p className="text-white font-bold text-sm">{slot.label}</p>
          <p className="text-zinc-500 text-[11px]">{slot.desc}</p>
        </div>
        {cover && <button onClick={remove} className="text-red-400 hover:text-red-300 text-xs" data-testid={`cover-del-${slot.id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>

      {hasSlides && (
        <div className="relative rounded-lg overflow-hidden mb-3 h-32 bg-black" data-testid={`cover-preview-${slot.id}`}>
          <CoverMedia cover={cover} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent flex flex-col justify-center p-3">
            <p className="text-white font-black text-lg leading-tight">{title || slot.label}</p>
            {subtitle && <p className="text-zinc-300 text-xs mt-1">{subtitle}</p>}
            {ctaLabel && <button className="mt-2 bg-amber-500 text-black font-black text-[10px] px-3 py-1 rounded-full w-fit uppercase tracking-widest">{ctaLabel}</button>}
          </div>
        </div>
      )}

      <SlidesManager slot={slot} cover={cover} onSaved={onSaved} />
      <div className="space-y-2">
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Tit (ex: NEW ARRIVALS)" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-amber-500" data-testid={`cover-title-${slot.id}`} />
        <input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Sous-tit (ex: Top flavors, top quality)" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-amber-500" data-testid={`cover-subtitle-${slot.id}`} />
        <input value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} placeholder="Bouton (ex: EXPLORE NOW)" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-amber-500" data-testid={`cover-cta-${slot.id}`} />
        <div className="flex gap-2 items-center">
          <button onClick={save} disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-black px-4 py-1.5 rounded-lg text-xs disabled:opacity-40" data-testid={`cover-save-${slot.id}`}>
            {saving ? "..." : "Sove"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DashboardCoversPanel = () => {
  const [covers, setCovers] = useState({});
  const load = async () => {
    try { const r = await axios.get(`${API}/dashboard-covers`); setCovers(r.data.covers || {}); } catch {}
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-5" data-testid="dashboard-covers-panel">
      <div className="flex items-center gap-2 mb-2">
        <Flag className="w-5 h-5 text-amber-400" />
        <h3 className="text-white font-bold text-lg">Dashboard Cover Management</h3>
      </div>
      <p className="text-zinc-400 text-xs mb-4">4 slot cover. Cover peyi a toujou gen priyorite — si pa gen pou peyi a, General Cover la itilize.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {COVER_SLOTS.map(slot => <CoverEditor key={slot.id} slot={slot} cover={covers[slot.id]} onSaved={load} />)}
      </div>
    </div>
  );
};

// ============== MINI ANNOUNCEMENTS ==============
const NewArrivalPanel = () => {
  const [link, setLink] = useState(""); const [label, setLabel] = useState("");
  useEffect(() => { axios.get(`${API}/site-config`).then(r => { setLink(r.data.new_arrival_link || ""); setLabel(r.data.new_arrival_label || ""); }).catch(() => {}); }, []);
  const save = async () => {
    if (link && !/^https?:\/\//i.test(link.trim())) return toast.error("Le lien doit commencer par https://");
    try { await axios.post(`${API}/ceo/site-config`, { new_arrival_link: link, new_arrival_label: label }); toast.success("Lien « Explore more » enregistré"); } catch { toast.error("Erreur"); }
  };
  return (
    <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-5" data-testid="new-arrival-panel">
      <h3 className="text-white font-bold text-lg mb-1">Vape Yard — New Arrival</h3>
      <p className="text-zinc-400 text-xs mb-3">Lien ouvert quand on clique sur « Explore more ».</p>
      <div className="flex gap-2 flex-wrap">
        <input value={link} onChange={e => setLink(e.target.value)} placeholder="https://..." className="flex-1 min-w-[200px] bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-500" data-testid="new-arrival-link-input" />
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Texte du bouton (EXPLORE MORE)" className="w-56 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-500" data-testid="new-arrival-label-input" />
        <button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-black font-black text-sm px-4 py-2 rounded-lg" data-testid="new-arrival-save">Enregistrer</button>
      </div>
    </div>
  );
};

const AnnouncementsPanel = () => {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [cta, setCta] = useState("");
  const [ctaLink, setCtaLink] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  const videoRef = useRef();
  const onVideo = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) return toast.error("Vidéo trop grosse (max 15MB)");
    const fd = new FormData(); fd.append("file", f);
    setVideoBusy(true);
    try { const r = await axios.post(`${API}/ceo/media/upload`, fd); setVideoUrl(r.data.url); toast.success("Vidéo prête"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); } finally { setVideoBusy(false); }
  };
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBase64, setPreviewBase64] = useState("");
  const [previewMime, setPreviewMime] = useState("image/png");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    try { const r = await axios.get(`${API}/announcements`); setItems(r.data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Imaj twò gwo (max 10MB)");
    const dataUrl = await readFileAsBase64(f);
    setPreviewUrl(dataUrl);
    setPreviewBase64(dataUrl.split(",", 2)[1]);
    setPreviewMime(f.type || "image/png");
  };

  const resetForm = () => { setTitle(""); setBody(""); setCta(""); setCtaLink(""); setVideoUrl(""); setPreviewUrl(""); setPreviewBase64(""); setEditingId(null); };

  const save = async () => {
    if (!title.trim()) return toast.error("Tit obligatwa");
    setSaving(true);
    try {
      const payload = { title: title.trim(), body: body.trim(), cta_label: cta.trim(), cta_link: ctaLink.trim(), video_url: videoUrl };
      if (previewBase64) { payload.image_base64 = previewBase64; payload.mime_type = previewMime; }
      if (editingId) {
        await axios.patch(`${API}/ceo/announcements/${editingId}`, payload);
        toast.success("✅ Anons mete ajou");
      } else {
        await axios.post(`${API}/ceo/announcements`, payload);
        toast.success("✅ Anons pibliye");
      }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
    finally { setSaving(false); }
  };

  const edit = (it) => { setEditingId(it.id); setTitle(it.title); setBody(it.body || ""); setCta(it.cta_label || ""); setCtaLink(it.cta_link || ""); setVideoUrl(it.video_url || ""); setPreviewUrl(absUrl(it.image_url) || ""); setPreviewBase64(""); };
  const toggleActive = async (it) => { try { await axios.patch(`${API}/ceo/announcements/${it.id}`, { active: !(it.active !== false) }); load(); } catch {} };
  const remove = async (it) => { if (!window.confirm(`Efase anons "${it.title}"?`)) return; try { await axios.delete(`${API}/ceo/announcements/${it.id}`); toast.success("Efase"); load(); } catch {} };

  return (
    <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-5" data-testid="announcements-panel">
      <div className="flex items-center gap-2 mb-2">
        <Megaphone className="w-5 h-5 text-amber-400" />
        <h3 className="text-white font-bold text-lg">Mini Cover / Anons</h3>
      </div>
      <p className="text-zinc-400 text-xs mb-4">Mesaj ki parèt an ba Dashboard Stocker yo (seksyon "Latest Announcements"). Ou ka pibliye nouvo anons, modifye, oswa dezaktive yon anons.</p>

      {/* Form */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4 mb-4 space-y-2">
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Tit anons" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white outline-none focus:border-amber-500" data-testid="ann-title-input" />
        <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Mesaj / detay (opsyonèl)" rows={2} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white outline-none focus:border-amber-500" data-testid="ann-body-input" />
        <input value={cta} onChange={e => setCta(e.target.value)} placeholder="Bouton CTA (ex: WATCH NOW)" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white outline-none focus:border-amber-500" data-testid="ann-cta-input" />
        <input value={ctaLink} onChange={e => setCtaLink(e.target.value)} placeholder="Lien du bouton (https://...)" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white outline-none focus:border-amber-500" data-testid="ann-cta-link-input" />
        <div className="flex gap-2 items-center">
          <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={onVideo} className="hidden" />
          <button onClick={() => videoRef.current?.click()} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-amber-400 rounded-lg px-3 py-2 text-sm" data-testid="ann-video-btn">{videoBusy ? "Envoi…" : videoUrl ? "🎬 Vidéo ajoutée — changer" : "🎬 Ajouter une vidéo"}</button>
          {videoUrl && <button onClick={() => setVideoUrl("")} className="text-red-400 text-xs" data-testid="ann-video-remove">Retirer</button>}
        </div>
        <div className="flex gap-2 items-center">
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
          <button onClick={() => fileRef.current?.click()} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-amber-400 rounded-lg px-3 py-2 text-sm flex items-center gap-2 justify-center" data-testid="ann-pick-btn">
            <Upload className="w-4 h-4" /> {previewUrl ? "Chanje foto" : "Chwazi foto"}
          </button>
          {previewUrl && <img src={previewUrl} alt="preview" className="w-14 h-14 rounded-lg object-cover border border-amber-500/40" />}
          {editingId && <button onClick={resetForm} className="text-zinc-500 hover:text-white text-xs">Anile</button>}
          <button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-black font-black px-5 py-2 rounded-lg text-sm disabled:opacity-40" data-testid="ann-save-btn">
            {saving ? "..." : editingId ? "Mete ajou" : "Pibliye"}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {items.length === 0 && <p className="text-zinc-500 text-sm text-center py-6">Pa gen anons ankò.</p>}
        {items.map(it => (
          <div key={it.id} className={`bg-zinc-950/50 border rounded-xl p-3 flex gap-3 items-center ${it.active === false ? "border-zinc-800 opacity-60" : "border-amber-500/30"}`} data-testid={`ann-item-${it.id}`}>
            {it.image_url && <img src={absUrl(it.image_url)} alt={it.title} className="w-16 h-16 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm truncate">{it.title}</p>
              {it.body && <p className="text-zinc-400 text-xs truncate">{it.body}</p>}
              {it.cta_label && <span className="inline-block mt-1 bg-amber-500/20 text-amber-300 text-[10px] px-2 py-0.5 rounded-full font-bold">{it.cta_label}</span>}
              {it.active === false && <span className="ml-2 text-[10px] uppercase bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full">off</span>}
            </div>
            <button onClick={() => toggleActive(it)} title={it.active === false ? "Aktive" : "Dezaktive"} className="text-amber-400 hover:text-amber-300" data-testid={`ann-toggle-${it.id}`}>
              {it.active === false ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
            </button>
            <button onClick={() => edit(it)} className="text-cyan-400 hover:text-cyan-300" data-testid={`ann-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(it)} className="text-red-400 hover:text-red-300" data-testid={`ann-del-${it.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const CeoVisualManager = () => {
  return (
    <div className="space-y-4" data-testid="ceo-visual-manager">
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-4">
        <h2 className="text-white font-black text-xl flex items-center gap-2">
          <ImageIcon className="w-6 h-6 text-amber-400" /> Vizyèl Dashboard
        </h2>
        <p className="text-zinc-400 text-sm mt-1">Jere foto pwodwi, cover peyi, ak anons ki parèt sou Dashboard Stockers yo.</p>
      </div>
      <TrackingManagerPanel />
      <ProductImagesPanel />
      <PageVisualsPanel />
      <DashboardCoversPanel />
      <NewArrivalPanel />
      <AnnouncementsPanel />
    </div>
  );
};

export default CeoVisualManager;
